import { useEffect, useState, type ReactNode } from 'react';
import { MailReadingPanel } from './MailReadingPanel.js';
import { PlatformSettingsForm } from './PlatformSettingsForm.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Stethoscope } from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { ago } from '../../lib/format.js';
import { Badge, Button, Callout, Field, Input, Spinner, useToast } from '../../ui/index.js';
import { McpServerPanel, type McpServerView } from './McpServerPanel.js';

/**
 * The MCP servers this system talks to.
 *
 * ONE PANEL COMPONENT, RENDERED PER SERVER. The screen used to hold a
 * hand-written form for the RAG server; adding the Graph server produced a
 * second, and the remaining five in the estate would have produced five
 * more — each free to word a field differently or omit one the others had.
 * The list now comes from `MCP_SERVERS`, so a new server is an entry in a
 * registry rather than a form, a migration and a set of columns.
 *
 * Every server authenticates the same way and holds its OWN credentials.
 * What is shared is the pattern and this code, never a client id.
 */

type Source = 'platform' | 'environment' | 'unset';

interface Rag {
  environment: { value: string; source: Source };
  ingestToken: { set: boolean; hint: string; source: Source };
  enabled: boolean;
  canPush: boolean;
  canQuery: boolean;
}

interface Storage {
  account: { value: string; source: Source };
  container: { value: string; source: Source };
  prefix: { value: string; source: Source };
  tenantId: { value: string; source: Source };
  clientId: { value: string; source: Source };
  clientSecretSet: boolean;
  usingConnectionString: boolean;
  enabled: boolean;
}

interface Payload {
  rag: Rag;
  storage: Storage;
  servers: McpServerView[];
  updatedAt: string | null;
  updatedBy: string | null;
}


/**
 * Ask the directory a real question, here, rather than deploying and reading
 * a toast on a different screen.
 *
 * The panel above can show "configured" and "can authenticate" while every
 * lookup fails: those are about reaching the MCP server, and a separate Azure
 * AD token is what gets an answer out of it. This presses the whole path —
 * token, transport, permission — and prints whatever comes back, including
 * the failure verbatim. That message IS the diagnostic; a 401 from Entra, a
 * refused connection and a DNS failure all arrive here looking identical
 * until you read it.
 */
function DirectoryCheck() {
  const [result, setResult] = useState<{ ok: boolean; detail: string; ms: number } | null>(null);
  const check = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; detail: string; ms: number }>(
        '/api/v1/platform/directory/check',
        {},
      ),
    onSuccess: setResult,
    onError: (err: unknown) =>
      setResult({
        ok: false,
        ms: 0,
        detail: err instanceof ApiError ? err.message : 'the check could not be run',
      }),
  });

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={check.isPending}
        onClick={() => check.mutate()}
      >
        <Stethoscope aria-hidden className="h-3.5 w-3.5" />
        Test a lookup
      </Button>
      {result && (
        <p
          className={`text-2xs leading-relaxed ${result.ok ? 'text-ink-secondary' : 'text-danger'}`}
        >
          {result.detail}
          {result.ok ? ` · ${result.ms}ms` : ''}
        </p>
      )}
    </div>
  );
}


/**
 * Where attachments are stored.
 *
 * ON THIS SCREEN, not only in the chart, because the storage account is in a
 * different Entra tenant from the cluster — the pod's managed identity cannot
 * reach it, so a credential has to exist somewhere, and the value you would
 * most want to correct in a hurry should not need a deploy.
 *
 * A field shows where it came from. "Set here", "from the environment" and
 * "not set" look identical in a text box and mean three very different things
 * when an upload is failing.
 */
/**
 * Prove the storage connection from here.
 *
 * The same reasoning as the directory check above: the panel can say
 * "Configured" while every upload fails. Credentials can be perfect and the
 * role missing; the role can be assigned and not yet propagated; the
 * container can be named right and not exist. Each of those is a different
 * blade to go and fix, and only the provider's own message tells you which.
 */
function StorageCheck() {
  const [result, setResult] = useState<{ ok: boolean; detail: string; ms: number } | null>(null);
  const check = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; detail: string; ms: number }>('/api/v1/platform/storage/check', {}),
    onSuccess: setResult,
    onError: (err: unknown) =>
      setResult({
        ok: false,
        ms: 0,
        detail: err instanceof ApiError ? err.message : 'the check could not be run',
      }),
  });

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={check.isPending}
        onClick={() => check.mutate()}
      >
        <Stethoscope aria-hidden className="h-3.5 w-3.5" />
        Test the connection
      </Button>
      {result && (
        <p className={`text-2xs leading-relaxed ${result.ok ? 'text-ink-secondary' : 'text-danger'}`}>
          {result.detail}
          {result.ok ? ` · ${result.ms}ms` : ''}
        </p>
      )}
    </div>
  );
}

export function StoragePanel({
  storage,
  value,
  edit,
  onSave,
  dirty,
  saving,
  check,
}: {
  storage: Storage;
  value: (k: string) => string;
  edit: (k: string, v: string) => void;
  onSave: () => void;
  dirty: boolean;
  saving: boolean;
  /**
   * The connection test, passed IN rather than rendered here.
   *
   * It needs a query client; this panel does not, and keeping it pure is
   * what lets it be rendered and asserted on without standing up half the
   * app. The same arrangement `McpServerPanel` uses for its `extra` slot.
   */
  check?: ReactNode;
}) {
  const where = (f: { source: Source }) =>
    f.source === 'platform' ? 'Set here' : f.source === 'environment' ? 'From the environment' : undefined;

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <header className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">Attachment storage</h3>
          <p className="text-2xs text-ink-tertiary">
            Azure Blob. Applies to <strong className="text-ink-secondary">every group</strong>.
          </p>
        </div>
        <Badge tone={storage.enabled ? 'ok' : 'neutral'}>
          {storage.enabled ? 'Configured' : 'Not configured'}
        </Badge>
      </header>

      {storage.usingConnectionString && (
        <Callout tone="info" className="mb-3">
          A connection string is set in the environment and takes precedence over everything
          below. That is the local Azurite path; on a deployment these fields are what is used.
        </Callout>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Storage account" hint={where(storage.account)}>
          <Input
            value={value('storageAccount')}
            onChange={(e) => edit('storageAccount', e.target.value)}
            placeholder="esatributary"
          />
        </Field>
        <Field label="Container" hint={where(storage.container)}>
          <Input
            value={value('storageContainer')}
            onChange={(e) => edit('storageContainer', e.target.value)}
            placeholder="attachments"
          />
        </Field>
        <Field
          label="Base folder"
          hint="Keeps environments apart inside one container, e.g. qa"
        >
          <Input
            value={value('storagePrefix')}
            onChange={(e) => edit('storagePrefix', e.target.value)}
            placeholder="qa"
          />
        </Field>
        <Field label="Directory (tenant) ID" hint={where(storage.tenantId)}>
          <Input
            value={value('storageTenantId')}
            onChange={(e) => edit('storageTenantId', e.target.value)}
            placeholder="the storage account's tenant"
          />
        </Field>
        <Field label="Application (client) ID" hint={where(storage.clientId)}>
          <Input
            value={value('storageClientId')}
            onChange={(e) => edit('storageClientId', e.target.value)}
          />
        </Field>
        <Field
          label="Client secret"
          hint={storage.clientSecretSet ? 'Set — leave blank to keep it' : 'Not set'}
        >
          <Input
            type="password"
            value={value('storageClientSecret')}
            onChange={(e) => edit('storageClientSecret', e.target.value)}
            placeholder={storage.clientSecretSet ? '••••••••' : ''}
          />
        </Field>
      </div>

      <p className="mt-3 text-2xs leading-relaxed text-ink-tertiary">
        Files are stored at{' '}
        <code className="text-ink-secondary">
          {[value('storagePrefix') || '…', '{group}', '{ticket}', '{file}'].join('/')}
        </code>
        . The registration needs{' '}
        <strong className="text-ink-secondary">Storage Blob Data Contributor</strong> on the
        container — granted on the container, not the account, so a mistake here cannot reach
        anything else in it.
      </p>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {/* Save first: a test against unsaved edits would report on the old
            values and read as the change not having worked. */}
        {check ?? <span />}
        <Button variant="primary" onClick={onSave} disabled={!dirty} loading={saving}>
          Save
        </Button>
      </div>
    </section>
  );
}

export function PlatformTab() {
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => api.get<Payload>('/api/v1/platform/settings'),
  });

  /**
   * Unsaved edits, keyed `<server>.<field>` for a connection field and by the
   * plain name for a RAG-only one.
   *
   * Held separately from the loaded values so a half-typed secret is never
   * confused with a stored one — the API returns only whether a secret is
   * set, never the secret.
   */
  const [patch, setPatch] = useState<Record<string, string>>({});
  const [text, setText] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const st = q.data.storage;
    const seeded = (f: { value: string; source: Source }) => (f.source === 'unset' ? '' : f.value);
    const next: Record<string, string> = {
      ragEnvironment: q.data.rag.environment.source === 'unset' ? '' : q.data.rag.environment.value,
      storageAccount: seeded(st.account),
      storageContainer: seeded(st.container),
      storagePrefix: seeded(st.prefix),
      storageTenantId: seeded(st.tenantId),
      storageClientId: seeded(st.clientId),
      // Never seeded: the secret is not returned, and showing dots as though
      // they were the value invites somebody to save the dots back.
      storageClientSecret: '',
    };
    for (const s of q.data.servers) {
      next[`${s.key}.baseUrl`] = s.baseUrl.value;
      next[`${s.key}.clientId`] = s.clientId.value;
      next[`${s.key}.tokenUrl`] = s.tokenUrl.value;
      // Never seeded from the server: it is not returned, and showing a
      // placeholder as though it were the value would invite somebody to
      // save it back as literal dots.
      next[`${s.key}.clientSecret`] = '';
    }
    setText(next);
    setPatch({});
  }, [q.data]);

  const value = (k: string) => text?.[k] ?? '';
  const edit = (k: string, v: string) => {
    setText((t) => (t ? { ...t, [k]: v } : t));
    setPatch((p) => ({ ...p, [k]: v }));
  };

  const save = useMutation({
    mutationFn: () => {
      // Flat edit keys become the nested shape the API takes, so adding a
      // server never touches the request type.
      const servers: Record<string, Record<string, string>> = {};
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(patch)) {
        const dot = k.indexOf('.');
        if (dot === -1) {
          body[k] = v;
          continue;
        }
        const key = k.slice(0, dot);
        (servers[key] ??= {})[k.slice(dot + 1)] = v;
      }
      if (Object.keys(servers).length > 0) body.servers = servers;
      return api.patch('/api/v1/platform/settings', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-settings'] });
      toast.success('Saved');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'That could not be saved'),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">Settings could not be loaded.</p>;

  const rag = q.data!.rag;
  /** Is anything unsaved for this server? */
  const dirtyFor = (key: string) =>
    Object.keys(patch).some((k) => k.startsWith(`${key}.`)) ||
    (key === 'rag' && ['ragEnvironment', 'ragIngestToken'].some((k) => k in patch));

  return (
    <div className="space-y-4">
      {q.data!.updatedAt && (
        <p className="text-2xs text-ink-tertiary">
          Last changed {ago(q.data!.updatedAt)}
          {q.data!.updatedBy ? ` by ${q.data!.updatedBy}` : ''}
        </p>
      )}

      <StoragePanel
        storage={q.data!.storage}
        value={value}
        edit={edit}
        onSave={() => save.mutate()}
        dirty={Object.keys(patch).some((k) => k.startsWith('storage'))}
        saving={save.isPending}
        check={<StorageCheck />}
      />

      <PlatformSettingsForm groups={['model', 'gateway', 'mail', 'features']} />

      <MailReadingPanel />

      {q.data!.servers.map((server) => (
        <McpServerPanel
          key={server.key}
          server={server}
          value={(f) => value(`${server.key}.${f}`)}
          onChange={(f, v) => edit(`${server.key}.${f}`, v)}
          onSave={() => save.mutate()}
          dirty={dirtyFor(server.key)}
          saving={save.isPending}
          extra={
            server.key === 'rag' ? (
              <RagExtras rag={rag} value={value} edit={edit} />
            ) : server.key === 'graph' ? (
              <DirectoryCheck />
            ) : null
          }
          footer={
            server.key === 'rag' ? (
              <p>
                Applies to <strong className="text-ink-secondary">every group</strong>. Which
                collection each group publishes into is set per group, on its own Knowledge tab.
              </p>
            ) : server.key === 'graph' ? (
              <p>
                Reading a colleague&rsquo;s job title also needs an Azure AD permission —{' '}
                <strong className="text-ink-secondary">delegated User.ReadBasic.All</strong> for
                the per-ticket lookup, or{' '}
                <strong className="text-ink-secondary">application User.Read.All</strong> for the
                whole-firm sweep. Those live in the environment, not here.
              </p>
            ) : null
          }
        />
      ))}

      {!rag.canPush && rag.enabled && (
        <Callout tone="warn" icon={<AlertTriangle />}>
          The knowledgebase has an address but no ingest token, so approved articles will not
          publish. They will keep drafting and reviewing in the meantime.
        </Callout>
      )}
    </div>
  );
}

/**
 * The two settings that belong to the RAG server alone.
 *
 * An environment label and a second credential for its push plane. Kept out
 * of the shared panel because inventing a place for them in every server's
 * form would be shaping seven things around one.
 */
function RagExtras({
  rag,
  value,
  edit,
}: {
  rag: Rag;
  value: (k: string) => string;
  edit: (k: string, v: string) => void;
}) {
  return (
    <>
      <Field
        label="Environment label"
        hint="Shown on screens so it is obvious which one you are pointed at."
      >
        <Input
          value={value('ragEnvironment')}
          onChange={(e) => edit('ragEnvironment', e.target.value)}
          placeholder="QA"
          aria-label="Environment label"
        />
      </Field>

      <Field
        label="Ingest token"
        hint={
          rag.ingestToken.set
            ? `Stored, ending ${rag.ingestToken.hint}. Type a new one to replace it.`
            : 'Not set — approved articles will not publish.'
        }
      >
        <Input
          type="password"
          value={value('ragIngestToken')}
          onChange={(e) => edit('ragIngestToken', e.target.value)}
          placeholder={rag.ingestToken.set ? '••••••••' : ''}
          className="font-mono"
          aria-label="Ingest token"
        />
      </Field>
    </>
  );
}
